/**
 * The single React state layer over the engine. localStorage (via json_store) stays the source
 * of truth; this context is a thin cache plus the two ways state changes:
 *   - mutate(fn): a synchronous, in-place change to profile/run (add event, edit traits, …),
 *     immediately persisted with save_state.
 *   - runOp(op, params): a CPU-heavy engine op dispatched to the worker; the returned profile/run
 *     replace local state and the worker's storage delta is reconciled into localStorage.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { save_state, user_paths } from "../lib/json_store";
import {
  load_user_session,
  start_draft_user_session,
  start_or_resume_user,
  type Session,
} from "../lib/app_service";
import { write_json, user_draft_paths } from "../lib/json_store";
import {
  hydrate_session,
  persist_session,
  remember_session,
  on_sync_error,
} from "../lib/session_sync";
import { dynamo_configured } from "../lib/dynamo";
import { runEngineOp, type EngineResult } from "../worker/client";
import type { WorkerOp } from "../worker/protocol";
import type { Profile, RunData } from "../types";

interface ProfileState {
  activeUser: string | null;
  user_profile: Profile | null;
  run_data: RunData | null;
}

interface ProfileContextValue extends ProfileState {
  /** Apply a synchronous change to profile/run, persist it, and sync to DynamoDB. */
  mutate: (fn: (profile: Profile, run: RunData) => void) => void;
  /** Run a heavy engine op in the worker; updates state and storage on resolve. */
  runOp: (op: WorkerOp, params?: Record<string, any>) => Promise<EngineResult>;
  /** Open an existing person by slug (hydrates their data from DynamoDB). */
  openUser: (slug: string) => Promise<void>;
  /** Create a fresh draft person, store them in DynamoDB, and enter the session. */
  startNew: (args: NewPersonArgs) => Promise<Session>;
  /** Reload the active session from storage (after an external change). */
  reload: () => void;
  /** Leave the current person and return to intake. */
  exitUser: () => void;
  /** Last DynamoDB sync error (null when the last sync succeeded). */
  syncError: Error | null;
}

export interface NewPersonArgs {
  first_name: string;
  birth_date: import("../lib/datetime").PlainDate;
  lat: number;
  lon: number;
  certainty: string;
  entered_time: import("../lib/datetime").PlainTime | null;
  range_start: import("../lib/datetime").PlainTime | null;
  range_end: import("../lib/datetime").PlainTime | null;
  range_end_date: import("../lib/datetime").PlainDate | null;
  /** User-confirmed UTC offset (hours) — pins the birth offset for pre-1970 births where the
   *  tzdb DST projection is unreliable. Null = trust the live tzdb resolution. */
  tz_offset_override: number | null;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

/**
 * Worker ops that only COMPUTE for display and never change the saved profile (rising-sign
 * breakdown, holdout, audits, overviews). They must not write ANYTHING back — no DynamoDB
 * persist, no storage reconcile, no state swap. The state/storage rule is load-bearing, not
 * just an optimisation: these reads run CONCURRENTLY with write ops (the heatmap fires
 * psychology/breakdown while a candidate scan runs), and a later-resolving read-only op would
 * otherwise clobber the scan's results with the stale profile it was snapshotted from.
 */
const READ_ONLY_OPS = new Set<WorkerOp>([
  "rising_sign_breakdown",
  "rising_sign_confidence",
  "holdout_validation",
  "holdout_validation_per_sign",
  "psychology_invariants",
  "chart_overview",
  "candidate_audit",
  // The marked-times comparison page's scorer. Pure display (the marked list itself is edited
  // via mutate); read-only for the same clobber reason as the other display ops.
  "compare_marked_times",
  // The eras panel's timeline computation. Pure display (it never changes the saved profile —
  // era ANSWERS are persisted separately via mutate). It MUST be read-only: the panel re-fires it
  // on every search-window change, so a drill-down kicks off an era recompute concurrently with the
  // candidate scan. Snapshotted before the scan's times exist, a write-classed era op resolving
  // LAST would save+swap its stale, times-empty profile and wipe the freshly scanned candidates
  // (the exact "no candidate times after drilling into a rising sign" bug).
  "dasha_era_segments",
]);

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ProfileState>({
    activeUser: null,
    user_profile: null,
    run_data: null,
  });
  const [syncError, setSyncError] = useState<Error | null>(null);

  // Edits made while a WRITE engine op is in flight would otherwise be lost: the op resolves
  // with a profile snapshotted BEFORE the edit and save_state/setState would clobber it. Every
  // mutate that lands during a write op is queued on that op (scoped to the op's USER, so an
  // edit made after switching people is never replayed onto — or persisted under — the previous
  // person) and re-applied onto the op's returned profile before it is saved/swapped in.
  // Write ops are additionally SERIALIZED (writeOpChain): two concurrent scans would otherwise
  // resolve out of order and the later-dispatched one would overwrite the earlier one's results.
  const activeWriteOps = React.useRef(
    new Set<{ user: string; queued: Array<(profile: Profile, run: RunData) => void> }>(),
  );
  const writeOpChain = React.useRef<Promise<void>>(Promise.resolve());
  const activeUserRef = React.useRef<string | null>(null);
  useEffect(() => {
    activeUserRef.current = state.activeUser;
  }, [state.activeUser]);

  useEffect(() => on_sync_error((err) => setSyncError(err)), []);

  const applySession = useCallback((slug: string, s: Session) => {
    setState({ activeUser: slug, user_profile: s.user_profile, run_data: s.run_data });
  }, []);

  const mutate = useCallback((fn: (profile: Profile, run: RunData) => void) => {
    // Recorded OUTSIDE the setState updater (updaters can be double-invoked in StrictMode),
    // and only on ops belonging to the CURRENTLY active person.
    for (const op of activeWriteOps.current) {
      if (op.user === activeUserRef.current) op.queued.push(fn);
    }
    setState((prev) => {
      if (!prev.user_profile || !prev.run_data || !prev.activeUser) return prev;
      // Clone so React sees a new reference; engine fns mutate in place.
      const profile = structuredClone(prev.user_profile);
      const run = structuredClone(prev.run_data);
      fn(profile, run);
      const [user_path, run_path] = user_paths(prev.activeUser);
      save_state(user_path, run_path, profile, run);
      // Push to DynamoDB in the background (serialized per slug; errors surface via syncError).
      void persist_session(prev.activeUser).catch(() => {});
      return { ...prev, user_profile: profile, run_data: run };
    });
  }, []);

  const runOp = useCallback(
    async (op: WorkerOp, params?: Record<string, any>): Promise<EngineResult> => {
      const takeSnapshot = () =>
        new Promise<ProfileState>((resolve) => {
          setState((prev) => {
            resolve(prev);
            return prev;
          });
        });
      const readOnly = READ_ONLY_OPS.has(op);
      if (readOnly) {
        // Display-only: hand back the result, change nothing. Runs concurrently with anything.
        const snapshot = await takeSnapshot();
        if (!snapshot.user_profile || !snapshot.run_data || !snapshot.activeUser) {
          throw new Error("No active session");
        }
        return runEngineOp(op, snapshot.user_profile, snapshot.run_data, params, undefined, {
          skipReconcile: true,
        });
      }
      const execute = async (): Promise<EngineResult> => {
        // Snapshot INSIDE the serialized chain so an op queued behind another sees its result.
        const snapshot = await takeSnapshot();
        if (!snapshot.user_profile || !snapshot.run_data || !snapshot.activeUser) {
          throw new Error("No active session");
        }
        const opRec = { user: snapshot.activeUser, queued: [] as Array<(p: Profile, r: RunData) => void> };
        activeWriteOps.current.add(opRec);
        let res: EngineResult;
        try {
          res = await runEngineOp(op, snapshot.user_profile, snapshot.run_data, params, undefined, {
            skipReconcile: false,
          });
        } finally {
          // On failure the queued edits need no replay — mutate already saved/persisted them
          // and no result will clobber them; dropping the op record discards its queue.
          activeWriteOps.current.delete(opRec);
        }
        const profile = res.user_profile as Profile;
        const run = res.run_data as RunData;
        // Re-apply edits made (to this same person) while the op ran — see activeWriteOps above.
        for (const fn of opRec.queued) {
          try {
            fn(profile, run);
          } catch (err) {
            console.error("Failed to re-apply an edit made during an engine op:", err);
          }
        }
        const [user_path, run_path] = user_paths(snapshot.activeUser);
        save_state(user_path, run_path, profile, run);
        void persist_session(snapshot.activeUser).catch(() => {});
        // Swap state only if the same person is still open — a result landing after a person
        // switch must not put someone else's profile on screen (storage above is still correct:
        // it wrote under the op's own slug).
        setState((prev) =>
          prev.activeUser === snapshot.activeUser ? { ...prev, user_profile: profile, run_data: run } : prev,
        );
        return res;
      };
      // Serialize write ops: concurrent scans resolving out of order would overwrite each
      // other's saved results (the later-DISPATCHED one wins with a stale profile).
      const chained = writeOpChain.current.then(execute, execute);
      writeOpChain.current = chained.then(
        () => {},
        () => {},
      );
      return chained;
    },
    [],
  );

  const openUser = useCallback(
    async (slug: string) => {
      // Pull this person's base/run/times from DynamoDB into the in-memory backend, then load
      // them through the existing synchronous session path.
      const found = await hydrate_session(slug);
      if (!found) throw new Error("This person could not be found in the cloud store.");
      const s = load_user_session(slug);
      remember_session(slug, String(s.user_profile["first_name"] || slug));
      applySession(slug, s);
    },
    [applySession],
  );

  const startNew = useCallback(
    async (args: NewPersonArgs): Promise<Session> => {
      const make_draft = () =>
        start_draft_user_session(
          args.first_name,
          args.birth_date,
          args.lat,
          args.lon,
          args.certainty,
          args.entered_time,
          args.range_start,
          args.range_end,
          args.range_end_date,
          args.tz_offset_override,
        );
      let s = make_draft();
      // unique_user_paths de-dupes only against the in-memory backend, which is EMPTY in a fresh
      // tab — so a slug that already belongs to someone in DynamoDB would sail through and the
      // persist below would overwrite their cloud profile. Hydrating each colliding cloud profile
      // into the backend makes the next reservation skip it, exactly like a local collision.
      if (dynamo_configured()) {
        for (let guard = 0; guard < 50; guard += 1) {
          const slug = String(s.user_profile["slug"]);
          let taken: boolean;
          try {
            taken = await hydrate_session(slug);
          } catch {
            break; // cloud unreachable — keep the local reservation rather than block intake
          }
          if (!taken) break;
          s = make_draft(); // the hydrated profile now exists locally; re-reserve past it
        }
      }
      // Persist immediately as the canonical profile (the Streamlit draft is auto-committed
      // here: a static app has no separate "draft" review screen before events).
      save_state(s.user_path, s.run_path, s.user_profile, s.run_data);
      const draft = user_draft_paths();
      write_json(draft[0], s.user_profile);
      write_json(draft[1], s.run_data);
      const slug = String(s.user_profile["slug"]);
      // Record the name in this browser's resume list, and push the new person to DynamoDB.
      remember_session(slug, String(s.user_profile["first_name"] || slug));
      await persist_session(slug);
      applySession(slug, s);
      return s;
    },
    [applySession],
  );

  const reload = useCallback(() => {
    setState((prev) => {
      if (!prev.activeUser) return prev;
      const s = load_user_session(prev.activeUser);
      return { activeUser: prev.activeUser, user_profile: s.user_profile, run_data: s.run_data };
    });
  }, []);

  const exitUser = useCallback(() => {
    setState({ activeUser: null, user_profile: null, run_data: null });
  }, []);

  const value = useMemo<ProfileContextValue>(
    () => ({ ...state, syncError, mutate, runOp, openUser, startNew, reload, exitUser }),
    [state, syncError, mutate, runOp, openUser, startNew, reload, exitUser],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

export function useProfile(): ProfileContextValue {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error("useProfile must be used within ProfileProvider");
  return ctx;
}

// Keep start_or_resume_user reachable for callers that want resume-by-name semantics.
export { start_or_resume_user };
